<template>
  <Dialog
    :visible="visible"
    modal
    :closable="true"
    :show-header="false"
    :style="{ width: '1100px', maxWidth: '95vw', height: '92vh', maxHeight: '92vh' }"
    :content-style="{ padding: 0, borderRadius: '16px', height: '100%' }"
    :pt="{ root: { class: 'legacy-billing-root' } }"
    @update:visible="onVisibleChange"
  >
    <header class="legacy-billing__header">
      <div class="legacy-billing__header-text">
        <h2 class="legacy-billing__title">
          {{ t('billing.legacyBilling.title') }}
        </h2>
        <p class="legacy-billing__subtitle">
          {{ t('billing.legacyBilling.subtitle') }}
        </p>
      </div>
      <button
        type="button"
        class="legacy-billing__close"
        :aria-label="t('billing.legacyBilling.close')"
        @click="onClose"
      >
        <i class="fa-regular fa-xmark" />
      </button>
    </header>

    <div class="legacy-billing__body">
      <BillingV3Frame v-if="visible" />
    </div>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import BillingV3Frame from '@/components/billing/BillingV3Frame.vue';

/**
 * Modal that wraps the legacy Zend interface inside an iframe.
 *
 * Used by SubscriptionPage for shops with offerVersion = v3 when they
 * trigger actions that are not implemented on the new dashboard yet
 * (change plan, buy credits). The shop sees the full Zend "remoteplan"
 * page inside the modal, handles its action there, and we close on
 * dismiss.
 */
export default defineComponent({
  name: 'LegacyBillingDialog',

  components: { Dialog, BillingV3Frame },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:visible'],

  setup(_, { emit }) {
    const { t } = useI18n();

    const onClose = () => emit('update:visible', false);
    const onVisibleChange = (v: boolean) => { if (!v) emit('update:visible', false); };

    return { t, onClose, onVisibleChange };
  },
});
</script>

<style lang="scss" scoped>
.legacy-billing {
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 22px 28px 16px;
    gap: 24px;
    border-bottom: 1px solid #e2e8f0;
  }

  &__header-text { min-width: 0; }

  &__title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: #64748b;
    margin: 4px 0 0;
    line-height: 1.5;
  }

  &__close {
    border: none;
    background: transparent;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    cursor: pointer;
    transition: all 160ms ease;
    flex-shrink: 0;

    &:hover {
      background: #f1f5f9;
      color: #0f172a;
    }

    i { font-size: 1.125rem; }
  }

  &__body {
    flex: 1;
    overflow: hidden;
    padding: 14px 14px 20px;
    height: calc(92vh - 80px);
  }

  &__body :deep(.billing-v3-frame) {
    height: 100%;
  }

  &__body :deep(.billing-v3-frame__iframe) {
    height: 100%;
    min-height: 100%;
  }

  &__body :deep(.billing-v3-frame__header) {
    display: none;
  }
}
</style>

<style lang="scss">
.legacy-billing-root.p-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 80px -24px rgba(15, 23, 42, 0.32);
  display: flex;
  flex-direction: column;

  .p-dialog-content {
    padding: 0 !important;
    border-radius: 0;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}
</style>
